/**
 * Centralised query-key factory.
 *
 * Usage:
 *   useQuery({ queryKey: keys.products.list(), queryFn: ... })
 *   queryClient.invalidateQueries({ queryKey: keys.products.all() })
 *     → invalidates BOTH list and detail queries for products
 */
export const keys = {
  products: {
    all:    () => ['products'],
    list:   (params = {}) => [...keys.products.all(), 'list', params],
    detail: (id) => [...keys.products.all(), String(id ?? 'none')],
  },
  customers: {
    all:    () => ['customers'],
    // params object kept as-is in the key for cache separation
    list:   (params = {}) => [...keys.customers.all(), 'list', params],
    detail: (id) => [...keys.customers.all(), String(id ?? 'none')],
  },
  services: {
    all:    () => ['services'],
    list:   (params = {}) => [...keys.services.all(), 'list', params],
    detail: (id) => [...keys.services.all(), String(id ?? 'none')],
  },
  supplies: {
    all:    () => ['supplies'],
    list:   (params = {}) => [...keys.supplies.all(), 'list', params],
    detail: (id) => [...keys.supplies.all(), String(id ?? 'none')],
  },
  sales: {
    all:    () => ['sales'],
    list:   (params = {}) => [...keys.sales.all(), 'list', params],
    detail: (id) => [...keys.sales.all(), String(id ?? 'none')],
  },
  purchases: {
    all:    () => ['purchases'],
    list:   (params = {}) => [...keys.purchases.all(), 'list', params],
    detail: (id) => [...keys.purchases.all(), String(id ?? 'none')],
  },
  expenses: {
    all:    () => ['expenses'],
    list:   (params = {}) => [...keys.expenses.all(), 'list', params],
    detail: (id) => [...keys.expenses.all(), String(id ?? 'none')],
  },
  staff: {
    all:    () => ['staff'],
    list:   (params = {}) => [...keys.staff.all(), 'list', params],
    detail: (id) => [...keys.staff.all(), String(id ?? 'none')],
  },
  paymentMethods: {
    all:    () => ['payment-methods'],
    list:   (params = {}) => [...keys.paymentMethods.all(), 'list', params],
    detail: (id) => [...keys.paymentMethods.all(), String(id ?? 'none')],
  },
  metrics: {
    all:     () => ['metrics'],
    summary: (userId, startDate, endDate, employeeId = 'all') => [...keys.metrics.all(), 'summary', userId, startDate, endDate, employeeId],
    orgSummary: (orgId, startDate, endDate) => [...keys.metrics.all(), 'org-summary', orgId, startDate, endDate],
    topItems: (userId, startDate, endDate, type = 'all', limit = 5, employeeId = 'all') => [...keys.metrics.all(), 'top-items', userId, startDate, endDate, type, limit, employeeId],
  },
  dailySessions: {
    all:    () => ['daily-sessions'],
    list:   (params = {}) => [...keys.dailySessions.all(), 'list', params],
    summary: (params = {}) => [...keys.dailySessions.all(), 'summary', params],
  },
  staffCommissions: {
    all:    () => ['staff-commissions'],
    detail: (userId) => [...keys.staffCommissions.all(), String(userId ?? 'none')],
    mine:   () => [...keys.staffCommissions.all(), 'me'],
    customStaff: (type) => [...keys.staffCommissions.all(), 'custom-staff', type],
  },
  notifications: {
    all:    () => ['notifications'],
    list:   (params = {}) => [...keys.notifications.all(), 'list', params],
    unreadCount: () => [...keys.notifications.all(), 'unread-count'],
    preferences: () => [...keys.notifications.all(), 'preferences'],
    config: () => [...keys.notifications.all(), 'config'],
  },
  memberships: {
    all:    () => ['memberships'],
    plans:  () => [...keys.memberships.all(), 'plans'],
    active: () => [...keys.memberships.all(), 'active'],
    customer: (id) => [...keys.memberships.all(), 'customer', String(id ?? 'none')],
  },
};
